import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
// Imported directly, not through the components/Inputs barrel: the barrel pulls
// in MetricPicker, which adds an import cycle (see AiReadyEditor).
import Dropdown from 'components/Inputs/Dropdown';

import { AgentEvalContext } from './AgentEvalProvider';
import {
  conceptLabel,
  productLabel,
  requirementLabel,
  routedSummary,
  VERDICT_BADGES,
  VERDICT_LABELS,
  verdictOf,
} from './constants';
import {
  groupByProduct,
  sortChecks,
  splitByApplicability,
  summariseEval,
} from './summariseEval';

// The list pane: every check that can run on this account, sectioned by product.
// Checks that can't run here are hidden behind a count rather than left to fail
// forever on data the account doesn't have.

const ListDiv = styled.div`
  width: 380px;
  flex-shrink: 0;
  border-right: 1px solid var(--color-border-secondary);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const HeaderDiv = styled.div`
  padding: 16px 20px 12px 20px;
  border-bottom: 1px solid var(--color-border-secondary);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupLabel = styled.div`
  padding: 14px 20px 4px 20px;
`;

const RowButton = styled.button<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  text-align: left;
  border: 0;
  border-bottom: 1px solid var(--color-border-secondary);
  cursor: pointer;
  padding: 12px 20px;
  background-color: ${(props) =>
    props.isSelected ? 'var(--color-bg-secondary)' : 'transparent'};
`;

const HiddenDiv = styled.div`
  padding: 12px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EvalList = ({
  selectedId,
  onSelect,
  onNewCheck,
  target,
}: {
  selectedId?: string;
  onSelect: (checkId: string) => void;
  onNewCheck: () => void;
  target: AgentEval.RunTarget;
}) => {
  const { checks, isLoading, rights, runMany, runningIds, stopRunMany } =
    useContext(AgentEvalContext);
  const [productFilter, setProductFilter] = useState<
    AgentEval.ProductKey | 'all'
  >('all');
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const { applicable, hidden } = useMemo(
    () => splitByApplicability(checks),
    [checks],
  );
  const filtered = useMemo(
    () =>
      productFilter === 'all'
        ? applicable
        : applicable.filter((check) => check.products.includes(productFilter)),
    [applicable, productFilter],
  );
  const groups = useMemo(
    () =>
      groupByProduct(filtered).map((group) => ({
        ...group,
        checks: sortChecks(group.checks),
      })),
    [filtered],
  );
  const presentProducts = useMemo(
    () => [...new Set(applicable.flatMap((check) => check.products))],
    [applicable],
  );
  // Only the products this account actually has checks for — a filter that
  // selects nothing is worse than no filter.
  const productOptions = useMemo(
    (): DropdownOption[] => [
      {
        label: 'All products',
        value: 'all',
        isSelected: productFilter === 'all',
        onSelected: () => setProductFilter('all'),
      },
      ...presentProducts.map((product) => ({
        label: productLabel(product),
        value: product,
        isSelected: productFilter === product,
        onSelected: () => setProductFilter(product),
      })),
    ],
    [presentProducts, productFilter],
  );
  const summary = summariseEval({ checks: filtered });
  const isRunningAll = progress !== null;

  const runAll = () => {
    setProgress({ done: 0, total: filtered.length });
    runMany({
      checkIds: filtered.map((check) => check.id),
      target,
      onProgress: setProgress,
    }).finally(() => setProgress(null));
  };

  return (
    <ListDiv data-testid="agent-eval-list">
      <HeaderDiv>
        <Row centerAlign spaceBetween>
          <Typography.Header type="H5">Evaluation suite</Typography.Header>
          <Row centerAlign style={{ gap: 12 }}>
            <Button
              type="Ghost"
              size="Small"
              label="New check"
              isDisabled={!rights.canEdit}
              testId="agent-eval-new-check"
              onClick={onNewCheck}
            />
            {isRunningAll ? (
              <Button
                type="Danger Secondary"
                size="Small"
                label={`Stop (${progress?.done}/${progress?.total})`}
                testId="agent-eval-stop"
                onClick={stopRunMany}
              />
            ) : (
              <Button
                type="Ghost"
                size="Small"
                label="Run all"
                isDisabled={
                  filtered.length === 0 ||
                  runningIds.size > 0 ||
                  !rights.canEdit
                }
                testId="agent-eval-run-all"
                onClick={runAll}
              />
            )}
          </Row>
        </Row>

        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          {summary.total} checks · {summary.passing} passing · {summary.failing}{' '}
          failing · {summary.clarified} clarified · {summary.notRun} not run
          {summary.drifted > 0 ? ` · ${summary.drifted} changed` : ''}
        </Typography.Body>

        <Dropdown
          options={productOptions}
          selectedLabel={
            productFilter === 'all'
              ? 'All products'
              : productLabel(productFilter)
          }
          testId="agent-eval-product-filter"
        />
      </HeaderDiv>

      {isLoading && (
        <GroupLabel>
          <Typography.Body type="Body 13" color="var(--color-text-secondary)">
            Loading…
          </Typography.Body>
        </GroupLabel>
      )}

      {groups.map((group) => (
        <div key={group.product ?? 'untagged'}>
          <GroupLabel>
            <Typography.Body type="Label" color="var(--color-text-secondary)">
              {group.product ? productLabel(group.product) : 'Any product'} (
              {group.checks.length})
            </Typography.Body>
          </GroupLabel>
          {group.checks.map((check) => {
            const verdict = verdictOf(check);
            return (
              <RowButton
                key={`${group.product}-${check.id}`}
                type="button"
                isSelected={check.id === selectedId}
                onClick={() => onSelect(check.id)}
                data-testid={`agent-eval-row-${check.id}`}
              >
                <Typography.Body type="Body 13">
                  {check.question}
                </Typography.Body>
                <Row centerAlign style={{ gap: 6, flexWrap: 'wrap' }}>
                  <Badge
                    badgeType={VERDICT_BADGES[verdict]}
                    text={VERDICT_LABELS[verdict]}
                  />
                  {check.scope.kind === 'account' && (
                    <Badge badgeType="Purple" text="This account" />
                  )}
                  {check.ambiguous && (
                    <Badge badgeType="Default" text="Ambiguous" />
                  )}
                  {check.lastRun?.drift && (
                    <Badge badgeType="Yellow" text="Changed" />
                  )}
                  {routedSummary(check.lastRun) && (
                    <Typography.Body
                      type="Body 12"
                      data-testid={`agent-eval-row-routed-${check.id}`}
                    >
                      {routedSummary(check.lastRun)}
                    </Typography.Body>
                  )}
                  {check.requires.length > 0 && (
                    <Typography.Body
                      type="Body 12"
                      color="var(--color-text-secondary)"
                    >
                      {requirementLabel(check.requires)}
                    </Typography.Body>
                  )}
                </Row>
              </RowButton>
            );
          })}
        </div>
      ))}

      {hidden.length > 0 && (
        <HiddenDiv>
          <Button
            type="Ghost"
            size="Small"
            label={
              showHidden
                ? 'Hide the ones that do not apply'
                : `${hidden.length} checks do not apply here`
            }
            testId="agent-eval-hidden-toggle"
            onClick={() => setShowHidden(!showHidden)}
          />
          {showHidden &&
            hidden.map((check) => (
              <Typography.Body
                key={check.id}
                type="Body 12"
                color="var(--color-text-secondary)"
              >
                {check.question} — needs{' '}
                {[
                  ...check.applicability.missingProducts.map(productLabel),
                  ...check.applicability.missingConcepts.map(conceptLabel),
                ].join(', ')}
              </Typography.Body>
            ))}
        </HiddenDiv>
      )}
    </ListDiv>
  );
};

export default EvalList;
